import { OrderPopupContainer } from "./styled";
import { OrderModel } from "../../shared/models/order/order-item.model";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import {
  getAdditionalServices,
  getCountries,
  getCurrencies,
  getProductCategories,
  getProductStores,
  getRecipientDocumentTypes,
  getShippingMethods,
  getWeightUnits,
} from "../../../redux/slices/static-data.slice";
import React, { MutableRefObject, useEffect, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import {
  changeSidebarInOrders,
  changeSidebarWithNavigate,
} from "../../../redux/slices/sidebar.slice";
import {
  changeActionbarStatus,
  changeActionbarType,
  changeOpenOrderName,
} from "../../../redux/slices/actionbar.slice";
import {
  ActionbarStatus,
  ActionbarType,
} from "../../../redux/reducers/actionbar-reducers";
import { getMyEveryRecipient } from "../../../redux/slices/recipients.slice";
import { Recipient } from "../../shared/models/user/recipient/recipient";
import { createEmptyOrder } from "../../../redux/slices/new-order-slice";
import { MessageSections } from "../../components/message/message";
import { toast, ToastContainer } from "react-toastify";
import NewOrderPopup from "./new-order-popup";
import ExistingOrderPopup from "./existing-order-popup";
import { OutletContext } from "../../../router/router";
import { getCurrentUserData } from "../../../redux/slices/current-user.slice";
import ordersService from "../../../services/orders.service";
import { getMessage } from "../../shared/helper-functions";
import { POLAND_COUNTRY_ID } from "../../../constants";

const OrderPopup = ({ mainContentContainerRef }: OrderPopupProps) => {
  const [order, setOrder] = useState<OrderModel>();

  const [isReady, setIsReady] = useState<boolean>(false);
  const [isReadonly, setIsReadonly] = useState(true);

  const [isNewOrder, setIsNewOrder] = useState(true);

  const { newOrder } = useAppSelector((state) => state.newOrderReducer);
  const { allRecipients } = useAppSelector(
    (state) => state.recipientDataReducer,
  );
  const { currentUser } = useAppSelector((state) => state.currentUserReducer);

  const [orderValidationErrors, setOrderValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const [productValidationErrors, setProductValidationErrors] = useState<{
    [key: string]: { [key: string]: string };
  }>({});

  const { messageCache, languageId } = useAppSelector(
    (state) => state.messagesReducer,
  );
  const { currencies, weightUnits, countries } = useAppSelector(
    (state) => state.staticDataReducer,
  );

  const { orderId } = useParams();
  const outletContext = useOutletContext<OutletContext>();

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!orderId && newOrder) {
      setOrder(newOrder);
      setIsReady(true);
    }
  }, [newOrder]);

  useEffect(() => {
    const isNewOrder = !orderId;
    setIsNewOrder(isNewOrder);

    dispatch(changeSidebarInOrders(true));
    dispatch(changeSidebarWithNavigate(true));
    dispatch(changeActionbarType(ActionbarType.ORDERS));

    dispatch(getCurrencies());
    dispatch(getWeightUnits());
    dispatch(getMyEveryRecipient());
    dispatch(getCurrentUserData());
    dispatch(getCountries());
    dispatch(getShippingMethods());
    dispatch(getAdditionalServices());
    dispatch(getRecipientDocumentTypes());
    dispatch(getProductCategories());
    dispatch(getProductStores({}));

    return () => {
      dispatch(changeOpenOrderName(undefined));
      dispatch(changeActionbarStatus(ActionbarStatus.DEFAULT));
    };
  }, []);

  useEffect(() => {
    if (allRecipients && currentUser) {
      if (isNewOrder) {
        const userDefaultRecipient =
          allRecipients.find((recipient: Recipient) => recipient.isDefault) ||
          allRecipients.find((recipient: Recipient) => recipient.isMain);

        const userCountry = currentUser.locationId;
        const userDestinationWarehouse = userCountry?.warehouse[0];

        if (userDefaultRecipient) {
          dispatch(
            createEmptyOrder({
              country: countries[0],
              currency:
                countries[0] && countries[0].countryId === POLAND_COUNTRY_ID
                  ? currencies[3]
                  : currencies[2],
              weightUnit:
                weightUnits.find((unit) => unit.weightUnitId === 1) ||
                weightUnits[0],
              products: [],
              recipient: userDefaultRecipient,
              destinationWarehouse: userDestinationWarehouse,
            }),
          );

          setIsReady(true);
        }
      } else if (orderId) {
        loadAndSetOrder();
      }
    }
  }, [allRecipients, currentUser]);

  function loadAndSetOrder() {
    if (orderId) {
      Promise.allSettled([
        ordersService.getOrderById(+orderId),
        dispatch(getProductStores),
      ]).then(([orderResponse]) => {
        if (orderResponse.status === "fulfilled") {
          const order = orderResponse.value;

          setOrder(order);
          dispatch(changeOpenOrderName(order?.name));

          if (orderResponse && order?.trackRecord[0]) {
            const orderStateId = order?.trackRecord[0].state.orderStateId;
            const orderInEnteredState = orderStateId === 1;

            setIsReadonly(
              outletContext.isWarehouseWorker ? false : !orderInEnteredState,
            );

            setIsReady(true);
          }
        }
      });
    }
  }

  function handleOrderValidationChange(validations: { [key: string]: string }) {
    if (Object.keys(validations).length) {
      const element = document.getElementById("order_name");

      if (element) {
        element.focus();
      }
    }

    setOrderValidationErrors(validations);
  }

  function handleProductValidationChange(validations: {
    [key: string]: { [key: string]: string };
  }) {
    if (Object.keys(validations).length) {
      // should be changed to product index if multiple products are available

      const productValidations = validations[0];
      const element = document.getElementById(
        `${Object.keys(productValidations)[0]}-${0}`,
      );

      if (element) {
        element.focus();
      }
    }

    setProductValidationErrors(validations);
  }

  function showToast(success: boolean, withChanges: boolean) {
    if (success) {
      if (withChanges) {
        toast["success"](
          getMessage("saved", MessageSections.ORDERS, messageCache, languageId),
          {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          },
        );
      } else {
        toast["warn"](
          getMessage(
            "no_changes",
            MessageSections.ORDERS,
            messageCache,
            languageId,
          ),
          {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          },
        );
      }
    } else {
      toast["error"](
        getMessage(
          "something_went_wrong",
          MessageSections.ORDERS,
          messageCache,
          languageId,
        ),
        {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        },
      );
    }
  }

  if (order && isReady) {
    return (
      <>
        <OrderPopupContainer>
          {isNewOrder ? (
            <NewOrderPopup
              order={order}
              orderValidationErrors={orderValidationErrors}
              productValidationErrors={productValidationErrors}
              setOrderValidationErrors={handleOrderValidationChange}
              setProductValidationErrors={handleProductValidationChange}
              showToast={showToast}
              mainContentContainerRef={mainContentContainerRef}
            />
          ) : (
            <ExistingOrderPopup
              order={order}
              setOrder={setOrder}
              isReadonly={isReadonly}
              orderValidationErrors={orderValidationErrors}
              productValidationErrors={productValidationErrors}
              setOrderValidationErrors={handleOrderValidationChange}
              setProductValidationErrors={handleProductValidationChange}
              showToast={showToast}
              loadAndSetOrder={loadAndSetOrder}
              mainContentContainerRef={mainContentContainerRef}
            />
          )}
        </OrderPopupContainer>

        <ToastContainer />
      </>
    );
  } else {
    return <></>;
  }
};

interface OrderPopupProps {
  mainContentContainerRef: MutableRefObject<HTMLElement | null>;
}

export default OrderPopup;
